import { makeAutoObservable } from "mobx";
import { EncodedImage, NiimbotBluetoothClient, PrintTaskVersion } from "@mmote/niimbluelib";
import { notification } from "antd";

class LabelPrinterStorage {
  isPrinterConnected = false;
  isPrinting = false;
  btEnabled = false;
  client = new NiimbotBluetoothClient();
  checkConnectionInterval: NodeJS.Timeout | null = null;

  constructor() {
    makeAutoObservable(this);
    this.isBluetoothSupported().then((btEnabled) => {
      this.setBtStatus(btEnabled);
    });
  }

  checkIfPrinterConnected = () => {
    const isConnected = this.client.isConnected();
    if (isConnected !== this.isPrinterConnected) {
      this.setPrinterConnected(isConnected);
    }
    if (!isConnected && this.checkConnectionInterval) {
      clearInterval(this.checkConnectionInterval);
      this.checkConnectionInterval = null;
    }
  }

  setCheckConnectionInterval = () => {
    if (this.checkConnectionInterval) {
      clearInterval(this.checkConnectionInterval);
      this.checkConnectionInterval = null;
    }
    this.checkConnectionInterval = setInterval(this.checkIfPrinterConnected, 500);
  }

  setBtStatus = (btEnabled: boolean) => {
    this.btEnabled = btEnabled;
  }

  isBluetoothSupported = async () => {
    try {
      // @ts-ignore
      const res = await navigator.permissions.query({ name: 'bluetooth' });
    } catch (e) {
      console.error("Bluetooth possibly not supported", e);
    }

    // @ts-ignore
    const isBtEnabled = !!navigator.bluetooth?.requestDevice;
    this.setBtStatus(isBtEnabled);

    return isBtEnabled;
  }

  setPrinterConnected = (connected: boolean) => {
    this.isPrinterConnected = connected;
  }

  setIsPrinting = (isPrinting: boolean) => {
    this.isPrinting = isPrinting;
  }

  connectToPrinter = async () => {
    if (!this.btEnabled) {
      notification.error({ message: "Bluetooth is not supported" });
      return;
    }

    if (this.client.isConnected()) {
      this.setPrinterConnected(true);
      this.setCheckConnectionInterval();
      notification.success({ message: "Printer connected" });
      return;
    }

    try {
      await this.client.connect();
      this.setPrinterConnected(true);
      this.setCheckConnectionInterval();
      notification.success({ message: "Printer connected" });
    } catch (e) {
      console.error(e);
      this.setPrinterConnected(false);
      notification.error({ message: "Printer connection failed" });
    }
  }

  disconnectFromPrinter = async () => {
    if (!this.btEnabled) {
      notification.error({ message: "Bluetooth is not supported" });
      return;
    }

    try {
      await this.client.disconnect();
    } catch (e) {
      console.error(e);
    } finally {
      this.setPrinterConnected(this.client?.isConnected());
      if (this.checkConnectionInterval) {
        clearInterval(this.checkConnectionInterval);
        this.checkConnectionInterval = null;
      }
      notification.success({ message: "Printer disconnected" });
    }
  }

  printLabel = async (image: EncodedImage, quantity = 1) => {
    if (!image) {
      return;
    }

    this.setIsPrinting(true);

    const taskVersion = this.client.getPrintTaskVersion() ?? PrintTaskVersion.V3;
    await this.client.abstraction.print(taskVersion, image, { quantity, density: 1 });
    try {
      await this.client.abstraction.waitUntilPrintFinished(taskVersion, quantity);
    } catch (e) {
      console.error(e);
      notification.error({ message: "Print failed" });
    }
    try {
      await this.client.abstraction.printEnd();
    } catch (e) {
      console.error(e);
      notification.error({ message: "Print failed" });
    }
    this.setIsPrinting(false);
  };
}

const labelPrinterStorage = new LabelPrinterStorage();

export default labelPrinterStorage;
